import type { FieldPath, FieldValues, RegisterOptions } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';
import { TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';

export interface RhfTextFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  disableClipboard?: boolean;
  rules?: RegisterOptions<T>;
  textFieldProps?: Pick<
    TextFieldProps,
    | 'type'
    | 'placeholder'
    | 'autoComplete'
    | 'multiline'
    | 'rows'
    | 'sx'
    | 'InputProps'
    | 'InputLabelProps'
    | 'onBlur'
    | 'helperText'
  >;
}

function RhfTextField<T extends FieldValues>({
  name,
  label,
  required,
  disabled,
  disableClipboard,
  rules,
  textFieldProps,
}: RhfTextFieldProps<T>) {
  const { control } = useFormContext<T>();

  const {
    field: { ref, ...field },
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: {
      ...rules,
      ...(required
        ? {
            required: label ? `${label} is required` : 'This field is required',
          }
        : {}),
    },
  });

  const {
    onBlur: onBlurProp,
    helperText: helperTextProp,
    ...restTextFieldProps
  } = textFieldProps ?? {};

  const preventClipboard = (event: React.ClipboardEvent) => {
    event.preventDefault();
  };

  return (
    <TextField
      {...field}
      inputRef={ref}
      value={field.value ?? ''}
      label={label}
      required={required}
      disabled={disabled}
      fullWidth
      size="small"
      variant="outlined"
      error={!!error}
      helperText={error?.message ?? helperTextProp}
      onBlur={(event) => {
        field.onBlur();
        onBlurProp?.(event);
      }}
      onCut={disableClipboard ? preventClipboard : undefined}
      onCopy={disableClipboard ? preventClipboard : undefined}
      onPaste={disableClipboard ? preventClipboard : undefined}
      {...restTextFieldProps}
    />
  );
}

export default RhfTextField;
